import React, { useState, useEffect } from "react"
import {
  CompositeScoreSet,
  useCreateCompositeScoreSetMutation,
  useUpdateCompositeScoreSetMutation,
} from "../../../app/services/meseeknaApiV2"

type ScoreSetFormModalProps = {
  scoreSet: CompositeScoreSet | null // null = create mode
  isOpen: boolean
  onClose: () => void
  onSave: () => void
}

const ScoreSetFormModal: React.FC<ScoreSetFormModalProps> = ({
  scoreSet,
  isOpen,
  onClose,
  onSave,
}) => {
  const isCreating = scoreSet === null
  const [name, setName] = useState("")
  const [error, setError] = useState<string | null>(null)

  const [createSet, { isLoading: isCreating2 }] = useCreateCompositeScoreSetMutation()
  const [updateSet, { isLoading: isUpdating }] = useUpdateCompositeScoreSetMutation()

  const isLoading = isCreating2 || isUpdating

  useEffect(() => {
    if (scoreSet) {
      setName(scoreSet.name || "")
    } else {
      setName("")
    }
    setError(null)
  }, [scoreSet, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError("Name is required")
      return
    }

    try {
      if (isCreating) {
        await createSet({ name: name.trim() }).unwrap()
      } else if (scoreSet) {
        await updateSet({ id: scoreSet.id, name: name.trim() }).unwrap()
      }
      onSave()
    } catch (err) {
      console.error("Failed to save score set:", err)
      setError("Failed to save score set. Please try again.")
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {isCreating ? "Create Score Set" : "Edit Score Set"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 cursor-pointer"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter score set name"
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300 cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 cursor-pointer disabled:opacity-50"
            >
              {isLoading ? "Saving..." : isCreating ? "Create" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ScoreSetFormModal
